import { ElementRef, useCallback, useMemo, useState } from 'react';

import { GOALS_OVERVIEW_PAGE_METRIC_CARDS } from 'Deer/metricCards/constants';
import { calculateProgressPercentages } from 'Deer/pages/MyData/Overview/cards/GoalsOverview/helper';
import { AgGridReact } from 'ag-grid-react';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';

import { OKR_TYPES } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';

const COLOR_MAP = {
  ON_TRACK: '#B1E39F',
  OFF_TRACK: '#FFDE88',
  AT_RISK: '#FFE1D9',
  CLOSED: '#CCCCCC',
  CLOSED_ACHIEVED: '#DBF2D3',
  CLOSED_DROPPED: '#EAEAEA',
  CLOSED_NOT_ACHIEVED: '#FFE1D9',
};

const TEXT_MAP = {
  ON_TRACK: 'On-track',
  OFF_TRACK: 'Off-track',
  AT_RISK: 'At-risk',
  CLOSED: 'Closed',
  CLOSED_ACHIEVED: 'Closed & achieved',
  CLOSED_DROPPED: 'Closed & dropped',
  CLOSED_NOT_ACHIEVED: 'Closed & not achieved',
};

const TEXT_COLOR_MAP = {
  ON_TRACK: '#44812F',
  OFF_TRACK: '#B96400',
  AT_RISK: '#C62C02',
  CLOSED: '#333333',
  CLOSED_ACHIEVED: '#1A5724',
  CLOSED_DROPPED: '#666666',
  CLOSED_NOT_ACHIEVED: '#A12402',
};

export const useGroupByGoalsData = ({ gridRef }: { gridRef: ElementRef<typeof AgGridReact> }) => {
  const { moduleName } = useOrganisationSettings();

  const [chartData, setChartData] = useState<{
    isLoading: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rowData: any[];
  }>({
    isLoading: true,
    rowData: [],
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [rowData, setRowData] = useState<Array<any>>([]);

  const [cardKPIData, setCardKPIData] = useState<
    {
      title: string;
      value: number | string;
      cardId: string;
    }[]
  >([]);

  // Calculating progress percentages for the donut chart
  const progressCountsForChart = rowData && calculateProgressPercentages(rowData);

  // Mapping progress data for the donut chart
  const donutChartData = useMemo(
    () =>
      progressCountsForChart?.map((data) => ({
        label: TEXT_MAP[data.status],
        labelColor: TEXT_COLOR_MAP[data.status],
        value: data.percentage,
        color: COLOR_MAP[data.status],
      })),
    [progressCountsForChart]
  );

  const onModalUpdated = useCallback(() => {
    const newRowData = [];
    // @ts-expect-error current is possibly null
    gridRef?.current?.api?.forEachNodeAfterFilterAndSort((row) => {
      newRowData.push(row?.data);
    });
    const totalKeyResults = newRowData.filter((row) => row.type === OKR_TYPES.KEY_RESULT);
    const keyResultsWithNoCheckIns = totalKeyResults.filter((row) => !row.lastCheckIn);
    const keyResultsAtRisk = totalKeyResults.filter((row) => row.progressLevel === 'AT_RISK');
    const sumOfAllProgresses = totalKeyResults.reduce((prev, cur) => (cur?.avgProgressPercentage || 0) + prev, 0);
    const avgProgressPercentage = sumOfAllProgresses / (totalKeyResults.length || 1);

    setCardKPIData([
      {
        title: `${modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE)} count`,
        value: totalKeyResults.length,
      },
      {
        title: `${modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE)} with no check-ins`,
        value: keyResultsWithNoCheckIns.length,
      },
      {
        cardId: GOALS_OVERVIEW_PAGE_METRIC_CARDS.myData.goalsAtRisk,
        title: `${modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE)} at-risk`,
        value: keyResultsAtRisk.length,
      },
      {
        title: `Average progress`,
        value: `${avgProgressPercentage?.toFixed(2) || 0}%`,
      },
    ]);
  }, [gridRef, moduleName]);

  return {
    chartData,
    setChartData,
    cardKPIData,
    isLoading: chartData.isLoading,
    onModalUpdated,
    setRowData,
    donutChartData,
  };
};
